import React, { useRef, useState } from "react";
import { Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * CSV import button + hidden file input.
 *
 * Props:
 *   endpoint    – e.g. "/parties/import" or "/items/import"
 *   label       – button text (defaults to "Import CSV")
 *   sampleName  – if provided, renders a small "Download template" link
 *                 that generates a client-side CSV with these headers.
 *   onImported  – () => void; called after a successful import so the parent
 *                 can refresh its list.
 *   testid      – data-testid prefix. `${testid}-btn` on the button,
 *                 `${testid}-input` on the hidden file input.
 */
export default function CsvImport({
  endpoint, label = "Import CSV", sampleName, sampleHeaders,
  onImported, testid = "csv-import",
}) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please choose a .csv file"); return;
    }
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(endpoint, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const msg = `Imported ${data.created}, skipped ${data.skipped}` +
        (data.errors?.length ? `, ${data.errors.length} error(s)` : "");
      toast.success(msg);
      if (data.errors?.length) console.warn("CSV import errors:", data.errors);
      onImported?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally { setBusy(false); }
  };

  const downloadSample = () => {
    if (!sampleHeaders?.length) return;
    const blob = new Blob([sampleHeaders.join(",") + "\n"], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = (sampleName || "template") + ".csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef} type="file" accept=".csv" className="hidden"
        data-testid={`${testid}-input`}
        onChange={onFile}
      />
      <button
        data-testid={`${testid}-btn`}
        className="btn-secondary"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <Upload size={14}/> {busy ? "Importing…" : label}
      </button>
      {sampleHeaders?.length > 0 && (
        <button
          data-testid={`${testid}-template`}
          className="btn-ghost text-xs"
          onClick={downloadSample}
        >
          <Download size={12}/> Template
        </button>
      )}
    </div>
  );
}
